import { Link, usePage } from "@inertiajs/react"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import { DYNAMIC_INVITES_FEATURE } from "@/features/staff/table-status"
import { tableTone } from "@/lib/status-colors"
import { t } from "@/lib/i18n"
type Table = {
    id: number
    externalId: string
    name: string
    seats: number
    status: string
    statusLabel: string
    hasActiveInvite: boolean
    hasStaticInvite: boolean
}
type Props = {
    tables: Table[]
    // Shared by Staff::Location::BaseController for every staff page.
    enabledFeatures?: string[]
}
function TableCard({ table, dynamicInvitesOn }: {
    table: Table
    dynamicInvitesOn: boolean
}) {
    return (<Link href={`/manage/tables/${table.externalId}`} className="block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
      <Card data-testid={`table-card-${table.id}`} density="compact" className="h-full border-border/80 transition-colors hover:border-primary/40 hover:bg-muted/20">
        <CardContent className="flex h-full flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h2 className="text-base font-semibold leading-tight">{table.name}</h2>
              <p className="text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.index.table_restaurant")}</p>
            </div>
            <StatusBadge
              tone={tableTone(table.status)}
              label={table.statusLabel}
              data-testid={`table-status-${table.id}`}
            />
          </div>

          <div className="grid gap-3 text-sm sm:grid-cols-2">
            <div className="space-y-1">
              <p className="text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.index.seats")}</p>
              <p className="font-medium" data-testid={`table-seats-${table.id}`}>{table.seats}</p>
            </div>
            {dynamicInvitesOn && (<div className="space-y-1" data-testid={`table-invite-status-${table.id}`}>
              <p className="text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.index.invite")}</p>
              <p className="font-medium">{table.hasActiveInvite ? t("ui.staff.tables.active_invite") : t("ui.staff.tables.no_invite")}</p>
            </div>)}
            {/* Ungated, unlike the dynamic invite above: the printed QR is the
                MVP entry path, not the opt-in one. */}
            <div className="space-y-1">
              <p className="text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.index.printed_qr")}</p>
              <p className="font-medium" data-testid={`table-static-invite-${table.id}`}>{table.hasStaticInvite ? t("ui.staff.tables.static_invite") : t("ui.staff.tables.no_static_invite")}</p>
            </div>
          </div>

          <p className="mt-auto text-xs text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.index.tap_view_detail_table")}</p>
        </CardContent>
      </Card>
    </Link>)
}
export default function StaffLocationManageTablesIndex() {
    const { tables, enabledFeatures = [] } = usePage<Props>().props
    const dynamicInvitesOn = enabledFeatures.includes(DYNAMIC_INVITES_FEATURE)
    return (<StaffLayout>
      <MainContainer size="md">
        <div className="space-y-6" data-testid="tables-management-view">
          <PageHeader title={t("ui.react.pages.staff.location.manage.tables.index.management_tables")} description={t("ui.react.pages.staff.location.manage.tables.index.manage_tables_available_restaurant")} actions={(<Button asChild data-testid="add-table-button">
                <Link href="/manage/tables/new">{t("ui.react.pages.staff.location.manage.tables.index.add_table")}</Link>
              </Button>)}/>

          {tables.length === 0 ? (<div data-testid="tables-empty-state" className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">{t("ui.react.pages.staff.location.manage.tables.index.no_tables")}</div>) : (<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {tables.map((table) => (<TableCard key={table.id} table={table} dynamicInvitesOn={dynamicInvitesOn}/>))}
            </div>)}
        </div>
      </MainContainer>
    </StaffLayout>)
}
